import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getClimber, updateClimberFields } from '../../services/firebase/climber';
import { getGym } from '../../services/firebase/gym';
import { Climber } from '../../models/user/Climber';
import { Gym } from '../../models/gym/Gym';
import HamburgerMenu from '../../components/HamburgerMenu';
import '../../styles/ClimberDetail.css';

interface GymWithRole {
  id: string;
  name: string;
  role: string;
}

const ClimberDetail: React.FC = () => {
  const { climberId } = useParams<{ climberId: string }>();
  const [climber, setClimber] = useState<Climber | null>(null);
  const [gymRoles, setGymRoles] = useState<GymWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Check access and load climber data
  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      if (!currentUser || !climberId) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      try {
        // Check if current user has 'free' role
        const adminClimber = await getClimber(currentUser.uid);
        if (!adminClimber || !adminClimber.gymMemberships) {
          setHasAccess(false);
          setLoading(false);
          return;
        }

        const hasFreeRole = Object.values(adminClimber.gymMemberships).some(
          role => role === 'fre'
        );
        
        setHasAccess(hasFreeRole);
        
        if (!hasFreeRole) {
          setLoading(false);
          return;
        }

        // Load climber data
        const climberData = await getClimber(climberId);
        if (!climberData) {
          setError('Climber not found');
          setLoading(false);
          return;
        }
        
        setClimber(climberData);

        // Load gym details for each gym membership
        const gymIds = climberData.getGymIds();
        const gymsWithRoles: GymWithRole[] = await Promise.all(
          gymIds.map(async (gymId) => {
            const gym = await getGym(gymId);
            const role = climberData.getRoleInGym(gymId) || 'climber';
            
            return {
              id: gymId,
              name: gym ? gym.name || 'Unknown Gym' : 'Unknown Gym',
              role: role
            };
          })
        );
        
        setGymRoles(gymsWithRoles);
        setError(null);
      } catch (err) {
        console.error('Error loading climber data:', err);
        setError('Failed to load climber data');
      } finally {
        setLoading(false);
      }
    };

    checkAccessAndLoadData();
  }, [currentUser, climberId]);

  const handleRoleChange = (gymId: string, newRole: string) => {
    setGymRoles(prevRoles => 
      prevRoles.map(gym => 
        gym.id === gymId ? { ...gym, role: newRole } : gym
      )
    );
  };

  const handleSaveChanges = async () => {
    if (!climber || !climberId) return;
    
    try {
      setSaving(true);
      setSuccessMessage(null);
      
      // Update climber's gym memberships
      const updatedMemberships = { ...climber.gymMemberships };
      
      gymRoles.forEach(gym => {
        updatedMemberships[gym.id] = gym.role;
      });
      
      // Save to Firestore
      await updateClimberFields(climberId, { 
        gymMemberships: updatedMemberships 
      });
      
      // Update local state
      if (climber instanceof Climber) {
        const updatedClimber = new Climber(climber.firebaseId, {
          ...climber,
          gymMemberships: updatedMemberships
        });
        setClimber(updatedClimber);
      }
      
      setSuccessMessage('Changes saved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating climber roles:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="climber-detail-container">
        <div className="climber-detail-header">
          <h1>Climber Details</h1>
          <HamburgerMenu />
        </div>
        <div className="loading-indicator">Loading...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="climber-detail-container">
        <div className="climber-detail-header">
          <h1>Climber Details</h1>
          <HamburgerMenu />
        </div>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <button onClick={() => navigate('/gyms')}>Go to Gyms</button>
        </div>
      </div>
    );
  }

  if (!climber) {
    return (
      <div className="climber-detail-container">
        <div className="climber-detail-header">
          <h1>Climber Details</h1>
          <HamburgerMenu />
        </div>
        <div className="error-message">{error || 'Climber not found'}</div>
        <button 
          className="back-button"
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="climber-detail-container">
      <div className="climber-detail-header">
        <button 
          className="back-button"
          onClick={() => navigate('/dashboard')}
        >
          ← Back
        </button>
        <h1>Climber Details</h1>
        <HamburgerMenu />
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="climber-profile">
        <div className="climber-avatar-large">
          {climber.imageUrl ? (
            <img 
              src={climber.imageUrl} 
              alt={climber.username || 'User'} 
            />
          ) : (
            <div className="avatar-placeholder-large">
              {(climber.username || climber.name || 'U').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="climber-info-large">
          <h2>{climber.username || 'Unknown User'}</h2>
          {climber.name && <p className="climber-full-name">{climber.name}</p>}
          <div className="climber-stats-box">
            <div className="stat">
              <span className="stat-label">Total Climbs</span>
              <span className="stat-value">{climber.getTotalClimbs()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Gyms</span>
              <span className="stat-value">{climber.getGymIds().length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Success Rate</span>
              <span className="stat-value">{climber.getSuccessRate().toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="gym-roles-section">
        <h3>Gym Memberships & Roles</h3>
        
        {gymRoles.length === 0 ? (
          <p>No gym memberships</p>
        ) : (
          <div className="gym-roles-list">
            {gymRoles.map(gym => (
              <div key={gym.id} className="gym-role-item">
                <div className="gym-role-info">
                  <span className="gym-name">{gym.name}</span>
                  <select 
                    value={gym.role}
                    onChange={(e) => handleRoleChange(gym.id, e.target.value)}
                    className="role-select"
                  >
                    <option value="climber">Climber</option>
                    <option value="setter">Setter</option>
                    <option value="owner">Owner</option>
                    <option value="fre">Fre</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="action-buttons">
          <button 
            className="save-button"
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClimberDetail;
